import { createStore, mergeRatings, persist, readJson, saveHooks } from "./store";
import type { CoreData, CountryId, Decision, SexPref, Verdict } from "../data/types";
import { isAvailable } from "../data/countries";
import { count } from "./telemetry";
import { nameKey } from "../data/keys";
import { pairKey, type Pairs } from "./nameoff";

// ------------------------------------------------------------------ settings

export type Theme = "system" | "light" | "dark";
/** pastel colour schemes; lilac is the default */
export type Palette = "lilac" | "blush" | "sky" | "sage" | "butter";
export const PALETTES: { id: Palette; label: string; swatch: string }[] = [
  { id: "lilac", label: "Lilac", swatch: "#d9cff3" },
  { id: "blush", label: "Blush", swatch: "#f5cfd9" },
  { id: "sky", label: "Sky", swatch: "#c9ddf3" },
  { id: "sage", label: "Sage", swatch: "#cfe3cc" },
  { id: "butter", label: "Butter", swatch: "#f2e3b4" },
];

export interface Settings {
  onboarded: boolean;
  /** whose records to swipe through */
  country: CountryId;
  sex: SexPref;
  surname: string;
  myName: string;
  theme: Theme;
  palette: Palette;
  haptics: boolean;
  reduceMotion: boolean;
  /** show a badge on cards your partner already liked (off = blind voting) */
  partnerHints: boolean;
  /** slip names your partner liked into your deck early */
  partnerFirst: boolean;
  /** skip names your partner has already turned down */
  hidePartnerNopes: boolean;
  /** now and then deal names like the ones you've liked (state/taste.ts) */
  tastePicks: boolean;
  /** send anonymous usage counts and error reports (see telemetry.ts): only once agreed to */
  shareUsage: boolean;
  /** whether the app has asked about usage counts yet */
  usageAsked: boolean;
  seed: number;
}

const SETTINGS_KEY = "nameling:settings";

/** A first guess from the device's time zone and language; onboarding asks to confirm it. */
export function detectCountry(): CountryId {
  let guess: CountryId = "other";
  try {
    guess = guessCountry(Intl.DateTimeFormat().resolvedOptions().timeZone || "", typeof navigator !== "undefined" ? navigator.language : "");
  } catch {
    /* no Intl time zone */
  }
  return isAvailable(guess) ? guess : "uk";
}

/** UK and US time zones mean those countries; a zone elsewhere means somewhere else. Without a
 * real time zone (UTC, as privacy modes report), the language's region decides. (index.html and
 * the preview page repeat this before the app loads.) */
export function guessCountry(tz: string, lang: string): CountryId {
  if (/^Europe\/(London|Belfast)$|^(GB|GB-Eire)$/.test(tz)) return "uk";
  if (/^America\/(New_York|Chicago|Denver|Phoenix|Los_Angeles|Anchorage|Adak|Boise|Detroit|Juneau|Sitka|Metlakatla|Yakutat|Nome|Menominee|Indiana\/.+|Kentucky\/.+|North_Dakota\/.+)$|^US\/|^Pacific\/Honolulu$/.test(tz)) return "us";
  if (tz && !/^(UTC|GMT|Etc\/.*)$/.test(tz)) return "other";
  if (/-GB$/i.test(lang)) return "uk";
  if (/-US$/i.test(lang)) return "us";
  return "other";
}

const saved = readJson<Partial<Settings> | null>(SETTINGS_KEY, null);

export const settings = createStore<Settings>({
  ...readJson<Omit<Settings, "country">>(SETTINGS_KEY, {
    onboarded: false,
    sex: "X",
    surname: "",
    myName: "",
    theme: "system",
    palette: "lilac",
    haptics: true,
    reduceMotion: false,
    partnerHints: false,
    partnerFirst: true,
    hidePartnerNopes: true,
    tastePicks: true,
    shareUsage: false,
    usageAsked: false,
    seed: Math.floor(Math.random() * 2 ** 31),
  }),
  // people who started before there was a choice of country were using the UK records
  country: isAvailable(saved?.country) ? saved.country : saved?.onboarded ? "uk" : detectCountry(),
});
persist(settings, SETTINGS_KEY);

// ------------------------------------------------------------------ filters

/** "known": ranked 101 to 1,000, familiar but not in every class */
export type Popularity = "all" | "top100" | "top500" | "top1000" | "known" | "uncommon" | "rare";
export type TrendFilter = "any" | "rising" | "steady" | "falling" | "new";
export type Character = "classic" | "vintage" | "unisex" | "scotland" | "ni" | "timeless";
export type SortMode = "smart" | "popular" | "rarest" | "az" | "rising" | "shuffle";

/** kinds of name someone can choose not to see */
export type HideType = "joined" | "alt" | "short" | "unisex" | "apostrophe";

export interface Filters {
  pop: Popularity;
  trend: TrendFilter;
  character: Character[];
  letters: string[];
  lenMin: number;
  lenMax: number;
  syl: number[];
  /** only these origins (empty = any) */
  origins: string[];
  /** never these origins */
  hideOrigins: string[];
  hide: HideType[];
  /** advanced search: text the name starts with, contains or ends with */
  nameText: string;
  nameMode: "starts" | "contains" | "ends";
  /** advanced search: a word in the meaning */
  meaningText: string;
  sort: SortMode;
  /** a curated collection from Explore, or null */
  collection: string | null;
  /** a fixed list of names from Explore, such as one year's top 100 */
  list: { title: string; keys: string[]; country?: CountryId } | null;
}

export const LEN_MIN = 2;
export const LEN_MAX = 12;

export const DEFAULT_FILTERS: Filters = {
  pop: "all",
  trend: "any",
  character: [],
  letters: [],
  lenMin: LEN_MIN,
  lenMax: LEN_MAX,
  syl: [],
  origins: [],
  hideOrigins: [],
  hide: [],
  nameText: "",
  nameMode: "starts",
  meaningText: "",
  sort: "smart",
  collection: null,
  list: null,
};

const FILTERS_KEY = "nameling:filters";
export const filters = createStore<Filters>(readJson<Filters>(FILTERS_KEY, DEFAULT_FILTERS));
persist(filters, FILTERS_KEY);

export function activeFilterCount(f: Filters): number {
  let n = 0;
  if (f.pop !== "all") n++;
  if (f.trend !== "any") n++;
  n += f.character.length ? 1 : 0;
  n += f.letters.length ? 1 : 0;
  if (f.lenMin !== LEN_MIN || f.lenMax !== LEN_MAX) n++;
  n += f.syl.length ? 1 : 0;
  n += f.origins.length ? 1 : 0;
  n += f.hideOrigins.length ? 1 : 0;
  n += f.hide.length;
  n += f.nameText.trim() ? 1 : 0;
  n += f.meaningText.trim() ? 1 : 0;
  if (f.collection) n++;
  if (f.list) n++;
  return n;
}

// ------------------------------------------------------------------ decisions

const DECISIONS_KEY = "nameling:decisions";

interface DecisionState {
  /** name key -> decision */
  map: Record<string, Decision>;
  /** bumps on every change so selectors can cheaply detect updates */
  rev: number;
}

function loadDecisions(): DecisionState {
  try {
    const raw = localStorage.getItem(DECISIONS_KEY);
    if (raw) return { map: JSON.parse(raw), rev: 1 };
  } catch {
    /* ignore */
  }
  return { map: {}, rev: 0 };
}

export const decisions = createStore<DecisionState>(loadDecisions());
// saved by name, so swipes made in another tab are merged in rather than saved over
persist(decisions, DECISIONS_KEY, {
  delay: 250,
  toSaved: (s) => s.map,
  fromSaved: (map, s) => ({ map: map as Record<string, Decision>, rev: s.rev + 1 }),
});

export interface UndoStep {
  key: string;
  prev: Decision | undefined;
  verdict: Verdict | null;
  /** where the card left the screen, so undo can bring it back from there */
  dir?: "left" | "right" | "up";
}

const undoStack: UndoStep[] = [];

export function decide(raw: string, verdict: Verdict | null, dir?: UndoStep["dir"], displayName?: string): void {
  // keys come from the names' data; anything else (a hand-made link) is put in the same form
  const key = nameKey(raw);
  if (!key) return;
  const prev = decisions.get().map[key];
  undoStack.push({ key, prev, verdict, dir });
  if (undoStack.length > 50) undoStack.shift();
  if (verdict !== null) count(verdict === 2 ? "decide.love" : verdict === 1 ? "decide.like" : "decide.nope");
  decisions.set((s) => {
    const map = { ...s.map };
    if (verdict === null) delete map[key];
    else map[key] = displayName ? { d: verdict, t: Date.now(), n: displayName } : { d: verdict, t: Date.now() };
    return { map, rev: s.rev + 1 };
  });
}

export function canUndo(): boolean {
  return undoStack.length > 0;
}

/** The name that undo would bring back. */
export function undoKey(): string | undefined {
  return undoStack[undoStack.length - 1]?.key;
}

export function undo(): UndoStep | null {
  const step = undoStack.pop();
  if (!step) return null;
  decisions.set((s) => {
    const map = { ...s.map };
    if (step.prev) map[step.key] = step.prev;
    else delete map[step.key];
    return { map, rev: s.rev + 1 };
  });
  return step;
}

export function verdictOf(key: string): Verdict | undefined {
  return decisions.get().map[key]?.d;
}

export function listByVerdict(map: Record<string, Decision>, verdicts: Verdict[]): string[] {
  return Object.keys(map)
    .filter((k) => verdicts.includes(map[k].d))
    .sort((a, b) => map[b].t - map[a].t);
}

/** Replace all decisions (import / restore / Start over). */
export function replaceDecisions(map: Record<string, Decision>): void {
  undoStack.length = 0;
  saveHooks.replaced?.(DECISIONS_KEY);
  decisions.set((s) => ({ map, rev: s.rev + 1 }));
}

// ------------------------------------------------------------------ name-off rankings

const RATINGS_KEY = "nameling:ratings";

export interface Ratings {
  elo: Record<string, number>;
  games: Record<string, number>;
  total: number;
  /** every Name-off answer, for the ranking it builds (state/nameoff.ts); absent before it existed */
  pairs: Pairs;
}

export const ratings = createStore<Ratings>(readJson<Ratings>(RATINGS_KEY, { elo: {}, games: {}, total: 0, pairs: {} }));
// two tabs' Name-offs combine name by name, so neither loses the other's picks
persist(ratings, RATINGS_KEY, { merge: mergeRatings });

/** Neither name won a Name-off pick: the one already placed stays ahead. */
export function recordTie(a: string, b: string): void {
  ratings.set((s) => ({ pairs: { ...s.pairs, [pairKey(a, b)]: 0 } }));
}

export function recordDuel(winner: string, loser: string): void {
  ratings.set((s) => {
    const elo = { ...s.elo };
    const games = { ...s.games };
    const rw = elo[winner] ?? 1500;
    const rl = elo[loser] ?? 1500;
    const expected = 1 / (1 + Math.pow(10, (rl - rw) / 400));
    const k = 32;
    elo[winner] = rw + k * (1 - expected);
    elo[loser] = rl - k * (1 - expected);
    games[winner] = (games[winner] ?? 0) + 1;
    games[loser] = (games[loser] ?? 0) + 1;
    const pairs = { ...s.pairs, [pairKey(winner, loser)]: winner < loser ? 1 : -1 };
    return { elo, games, total: s.total + 1, pairs };
  });
}

// ------------------------------------------------------------------ ui state (not persisted)

export type Tab = "swipe" | "list" | "explore" | "together";

export interface UiState {
  core: CoreData | null;
  loadError: string | null;
  /** another country's names are loading */
  switching: CountryId | null;
  /** open name detail sheet for this key */
  detail: string | null;
  filtersOpen: boolean;
  settingsOpen: boolean;
  nameOffOpen: boolean;
  /** pending "it's a match" celebration */
  match: string | null;
  /** `spoken`: the message is read out by the screen that showed it, so the toast stays quiet */
  toast: { id: number; text: string; action?: { label: string; run: () => void }; spoken?: boolean } | null;
  confirm: ConfirmRequest | null;
}

export interface ConfirmRequest {
  id: number;
  title: string;
  body?: string;
  ok: string;
  danger?: boolean;
  resolve: (ok: boolean) => void;
}

export const ui = createStore<UiState>({
  core: null,
  loadError: null,
  switching: null,
  detail: null,
  filtersOpen: false,
  settingsOpen: false,
  nameOffOpen: false,
  match: null,
  toast: null,
  confirm: null,
});

let confirmId = 0;
/** In-app replacement for window.confirm (which some embeds silently refuse). */
export function ask(title: string, opts: { body?: string; ok?: string; danger?: boolean } = {}): Promise<boolean> {
  ui.get().confirm?.resolve(false);
  return new Promise((resolve) =>
    ui.set({ confirm: { id: ++confirmId, title, body: opts.body, ok: opts.ok ?? "OK", danger: opts.danger, resolve } }),
  );
}

let toastId = 0;
let toastTimer = 0;
export function toast(text: string, action?: { label: string; run: () => void }, ms = 3200): void {
  clearTimeout(toastTimer);
  ui.set({ toast: { id: ++toastId, text, action } });
  // a message with a button stays long enough to reach the button
  toastTimer = window.setTimeout(() => ui.set({ toast: null }), action ? Math.max(ms, 10000) : ms);
}

/** A toast stays while someone is on its button, and goes a few seconds after they leave it. */
export function holdToast(hold: boolean): void {
  clearTimeout(toastTimer);
  if (!hold) toastTimer = window.setTimeout(() => ui.set({ toast: null }), 4000);
}

let saveWarned = false;
saveHooks.failed = () => {
  if (saveWarned) return;
  saveWarned = true;
  toast(
    __NATIVE__
      ? "Couldn't save on this device: its storage may be full"
      : "Couldn't save on this device: the browser's storage is full or turned off",
    undefined,
    6000,
  );
};

/** Share through the system sheet where there is one; otherwise copy to the clipboard. */
export async function shareOrCopy(data: ShareData, copyText: string, copied: string): Promise<void> {
  count("share.link");
  if (native.share && (await native.share(data))) return;
  // the claude.ai frame refuses Web Share, so the preview goes straight to copying
  if (navigator.share && !__PREVIEW__) {
    try {
      await navigator.share(data);
      return;
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
    }
  }
  try {
    await navigator.clipboard.writeText(copyText);
    toast(copied);
  } catch {
    toast(__NATIVE__ ? "Couldn't copy that" : "Couldn't copy: this browser blocked it");
  }
}

export function haptic(pattern: number | number[]): void {
  if (!settings.get().haptics) return;
  if (native.haptic) return native.haptic(pattern);
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* unsupported */
  }
}

/** Most people swipe on a phone; on a computer the copy shouldn't say so. */
export const onPhone = (): boolean => __NATIVE__ || matchMedia("(pointer: coarse)").matches;

/** The public website: links shared from the iOS app have to point somewhere a browser can open. */
export const SITE = "https://www.nameling.co.uk";
/** where the website lived before it had its own domain; it still serves the app for people whose names are saved there */
export const OLD_HOST = "nameling-uk.netlify.app";
// links people share point at the site's own address, from the old one too
export const siteOrigin = (): string => (__NATIVE__ || location.hostname === OLD_HOST ? SITE : location.origin);
/** where the partner API lives: the same site, except in the iOS app */
export const API_BASE = __NATIVE__ ? SITE : "";

/** Hooks the iOS app fills in at start-up (see native.ts); the website leaves them empty. */
export const native: {
  haptic?: (pattern: number | number[]) => void;
  share?: (data: ShareData) => Promise<boolean>;
  /** offer a file through the share sheet (Save Image, Save to Files...): false if closed */
  shareFile?: (filename: string, data: Blob) => Promise<boolean>;
  /** ask iOS for its rating prompt; iOS decides whether to show it */
  requestReview?: () => void;
  /** keep the status bar readable when the theme changes */
  theme?: (dark: boolean) => void;
  /** one notification on a date (the due-date reminder in state/chosen.ts) */
  reminder?: {
    /** ask iOS whether the app may notify: resolves whether it may */
    allow: () => Promise<boolean>;
    /** set the reminder, replacing any set before; tapping it opens `route` */
    set: (r: { at: Date; title: string; body: string; route: string }) => Promise<void>;
    cancel: () => Promise<void>;
  };
} = {};
